import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Globe2, CheckCircle2 } from "lucide-react";
import dayjs from "dayjs";

const DESTINATIONS = [
  "London, United Kingdom", "Paris, France", "Munich, Germany", "Frankfurt, Germany", "Rome, Italy", "Milan, Italy",
  "Madrid, Spain", "Barcelona, Spain", "Lisbon, Portugal", "Amsterdam, Netherlands", "Zurich, Switzerland", "Geneva, Switzerland",
  "Vienna, Austria", "Istanbul, Türkiye", "Athens, Greece", "Dublin, Ireland", "Edinburgh, United Kingdom", "Nice, France",
  "New York, United States", "Los Angeles, United States", "Miami, United States", "Toronto, Canada",
  "Riyadh, Saudi Arabia", "Jeddah, Saudi Arabia", "Doha, Qatar", "Muscat, Oman", "Kuwait City, Kuwait", "Manama, Bahrain",
  "Cape Town, South Africa", "Johannesburg, South Africa", "Melbourne, Australia", "Sydney, Australia", "Auckland, New Zealand",
];

const STEPS = ["Request received", "International desk review", "Partner confirmation", "Voucher issued to your email"];

export default function International() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ destination: "", pickup_date: dayjs().add(14, "day").format("YYYY-MM-DD"), days: 7, name: "", email: "", phone: "" });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/international-requests", { ...form, days: Number(form.days) });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 promo-stripe">
          <h1 className="heading-upper text-2xl sm:text-3xl text-white" data-testid="international-heading">{t("International rentals")}</h1>
          <p className="text-white/80 text-sm mt-2 max-w-xl">{t("Europcar operates in 140+ countries. Tell us where you're headed — our international desk arranges your booking through the global network, honestly tracked at every step.")}</p>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 w-full flex-1 grid md:grid-cols-[1fr_280px] gap-8">
        {done ? (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 text-center" data-testid="international-success">
            <CheckCircle2 className="w-12 h-12 text-[#009900] mx-auto mb-3" />
            <h2 className="heading-upper text-xl text-[#144733]">{t("Request {{id}} received", { id: done.request_id })}</h2>
            <p className="text-sm text-[#57685e] mt-2">{t("We're honest about timing: our international desk responds within 1 business day with availability and a firm quote for {{dest}}. Nothing is charged until you approve it.", { dest: done.destination })}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 space-y-4" data-testid="international-form">
            <div>
              <label className="field-label">{t("Destination *")}</label>
              <input required list="intl-destinations" className="field-input" placeholder={t("Start typing a city…")} value={form.destination}
                onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="intl-destination-input" />
              <datalist id="intl-destinations">
                {DESTINATIONS.map((d) => <option key={d} value={d} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="field-label">{t("Pickup date *")}</label>
                <input type="date" required className="field-input" min={dayjs().format("YYYY-MM-DD")} value={form.pickup_date}
                  onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} data-testid="intl-date-input" />
              </div>
              <div>
                <label className="field-label">{t("Rental length (days) *")}</label>
                <input type="number" min={1} max={90} required className="field-input" value={form.days}
                  onChange={(e) => setForm({ ...form, days: e.target.value })} data-testid="intl-days-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="field-label">{t("Full name *")}</label>
                <input required className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="intl-name-input" />
              </div>
              <div>
                <label className="field-label">{t("Email *")}</label>
                <input type="email" required className="field-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="intl-email-input" />
              </div>
              <div>
                <label className="field-label">{t("Phone *")}</label>
                <input required className="field-input" placeholder="+971 5X XXX XXXX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="intl-phone-input" />
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-cta rounded w-full py-3.5 text-sm" data-testid="intl-submit-btn">
              {busy ? t("Sending…") : t("Request my international quote")}
            </button>
          </form>
        )}

        <aside className="bg-white rounded-2xl border border-[#E2E8F0] p-5 h-fit" data-testid="intl-tracker">
          <h3 className="heading-upper text-sm tracking-widest text-[#144733] mb-4 flex items-center gap-2"><Globe2 className="w-4 h-4 text-[#009900]" /> {t("How it works")}</h3>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s} className="flex gap-3 text-sm text-[#0F1E17]">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${(done && i === 0) ? "bg-[#009900] text-white" : "border-2 border-[#d5ddd5] text-[#57685e]"}`}>{i + 1}</span>
                {t(s)}
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-[#8a978f] mt-4">{t("Typical turnaround: 1 business day. International bookings are fulfilled by Europcar partners in the destination country.")}</p>
        </aside>
      </main>
      <Footer />
    </div>
  );
}
